import tw from 'twin.macro';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';

import { SounderLogo } from '@icons/SounderLogo';
import { UserAvatar } from '@components/UserAvatar';
import { SolutionsMenu } from '@components/SolutionsMenu';
import { SCREENS, useMediaQueries } from '@app/hooks/useMediaQueries';
import { FEATURE_FLAG, useFeatureFlag } from '@auth_module/hooks/useFeatureFlag';
import { UserApplication } from '@auth_module/services';
import { InsightsApplicationIcon } from '@icons/InsightsApplicationIcon';
import { PromoHubApplicationIcon } from '@icons/PromoHubApplicationIcon';
import { ApiApplicationIcon } from '@icons/ApiApplicationIcon';
import { useInsightsContext } from '@insights_module/context/InsightsContext';
import { useAccountContext } from '@account_module/context/AccountContext';
import { searchAudios, searchGroups } from '@services/index';
import {
  AQLBrandSafetyGarmFilterQuery,
  AQLBrandSafetyGarmQuery,
} from '@insights_module/adc_ql/queries';
import { useLayoutContext } from '@insights_module/context/LayoutContext';
import { CloseIcon } from '@icons/CloseIcon';
import { NavIcon } from '@icons/NavIcon';

const HeaderWrapper = tw.div`w-full flex flex-row items-center justify-between h-[54px] border-b border-gray-3 bg-neutral-background shrink-0`;
const LeftWrapper = tw.div`flex flex-row items-center justify-start gap-2 cursor-pointer min-w-[254px] h-full sm:border-r sm:border-r-gray-3 pl-[24px]`;
const Separator = tw.div`w-[1px] min-w-[1px] h-[17px] bg-gray-3`;
const BetaLabel = tw.div`bg-sec-3 text-dark-accent-invert text-[11px] px-1 rounded-sm`;
const TitleWrapper = tw.div`flex justify-center items-center gap-[4px] cursor-pointer`;

const RightWrapper = tw.div`flex items-center justify-between w-full gap-[24px] px-[24px]`;
const HeaderTitle = tw.div`flex text-[14px] text-dark-accent-invert font-bold whitespace-nowrap`;
const HeaderPageTitle = tw.div`hidden sm:flex text-[18px] text-text-and-icon font-semibold`;
const HeaderInfoWrapper = tw.div`hidden sm:flex flex-row items-center text-[12px] text-text-and-icon-muted gap-[4px]`;

const HeaderBrandingContainer = tw.div`flex flex-row items-center justify-start gap-2 h-[54px] w-full`;
const MobileSidebarToggler = tw.button`min-w-[28px] min-h-[28px] bg-gray-1 flex items-center justify-center rounded-[4px]`;

export const HEADER_HEIGHT = 54;
export const TABLE_CONTROLLER_HEIGHT = 39;
const SETTINGS_PAGE_URL_PATH = '/account/settings';

const APPLICATION_TITLE_MAPPER = {
  promohub: {
    title: 'AI-Promotion Hub',
    icon: <PromoHubApplicationIcon />,
    label: 'beta',
  },
  'audio-insights': {
    title: 'Audio Insights',
    icon: <InsightsApplicationIcon />,
    label: 'beta',
  },
  api: {
    title: 'API',
    icon: <ApiApplicationIcon />,
    label: '',
  },
} as { [key in UserApplication]: { title: string; icon: JSX.Element; label: string } };

export const Header = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { currentApplication, currentPage } = useAccountContext();

  const { isEnabled: isInsightsOverviewPageEnabled } = useFeatureFlag(
    FEATURE_FLAG.INSIGHTS_OVERVIEW_PAGE
  );

  const { isEnabled: isAudioProcessingCountEnabled } = useFeatureFlag(
    FEATURE_FLAG.AUDIO_PROCESSING_COUNT
  );

  const { data: audiosData } = useQuery({
    queryKey: ['total_episodes_count_data'],
    queryFn: () =>
      searchAudios({
        page: 1,
        pageSize: 1,
        payload: {
          query: new AQLBrandSafetyGarmQuery([
            new AQLBrandSafetyGarmFilterQuery('adult_explicit_content', 'range', {
              gte: 'unrelated',
              lte: 'floor',
            }),
          ]),
        },
      }),
    staleTime: 60 * 60 * 1000,
  });

  const { data: groupsData } = useQuery({
    queryKey: ['total_shows_count_data'],
    queryFn: () =>
      searchGroups({
        page: 1,
        pageSize: 1,
        payload: {
          query: new AQLBrandSafetyGarmQuery([
            new AQLBrandSafetyGarmFilterQuery('adult_explicit_content', 'range', {
              gte: 'unrelated',
              lte: 'floor',
            }),
          ]),
        },
      }),
    staleTime: 60 * 60 * 1000,
  });

  const totalEpisodesCount = audiosData?.pagination?.total || 0;
  const totalShowsCount = groupsData?.pagination?.total || 0;
  const shouldDisplayTotalContentInfo =
    !location.pathname.includes(SETTINGS_PAGE_URL_PATH) || !isAudioProcessingCountEnabled;

  return (
    <>
      {/* this two HeaderWrappers composition is only while we are using this header,
     because we need to have max width set on overview page but not on others */}
      <HeaderWrapper
        css={[
          isInsightsOverviewPageEnabled && currentPage === 'overview' && tw`justify-center px-0`,
        ]}>
        <HeaderWrapper tw='border-none px-0'>
          <LeftWrapper>
            <HeaderBranding />
          </LeftWrapper>
          <RightWrapper>
            <div tw='flex flex-col'>
              <HeaderPageTitle>{t(`insights.header.page.${currentPage}`)}</HeaderPageTitle>

              {shouldDisplayTotalContentInfo && (
                <HeaderInfoWrapper>
                  <span tw='font-semibold'>Total content:</span>
                  <span>{totalShowsCount.toLocaleString()}</span>
                  <span>shows</span>
                  <span>•</span>
                  <span>{totalEpisodesCount.toLocaleString()}</span>
                  <span>audio files</span>
                </HeaderInfoWrapper>
              )}
            </div>

            <div tw='flex items-center justify-end gap-4'>
              <UserAvatar currentApplication={currentApplication} />
            </div>
          </RightWrapper>
        </HeaderWrapper>
      </HeaderWrapper>
    </>
  );
};

export const HeaderBranding = () => {
  const [isMobile] = useMediaQueries(SCREENS.SM);
  const { setSelectedShow } = useInsightsContext();
  const { currentApplication } = useAccountContext();
  const { isSidebarVisible, setIsSidebarVisible } = useLayoutContext();

  const handleGoToMainPage = () => {
    if (currentApplication === 'audio-insights') {
      setSelectedShow('');
    }
  };

  return (
    <HeaderBrandingContainer>
      {!isMobile && <SolutionsMenu />}
      {isMobile && (
        <MobileSidebarToggler onClick={() => setIsSidebarVisible(!isSidebarVisible)}>
          {isSidebarVisible ? (
            <CloseIcon tw='fill-text-and-icon' />
          ) : (
            <NavIcon tw='stroke-text-and-icon' />
          )}
        </MobileSidebarToggler>
      )}
      <SounderLogo width={20} height={28} tw='fill-dark-accent-invert min-w-[20px]' />
      <Separator />

      <TitleWrapper onClick={handleGoToMainPage}>
        <>
          {currentApplication && <>{APPLICATION_TITLE_MAPPER[currentApplication]?.icon}</>}
          <HeaderTitle>
            {currentApplication && APPLICATION_TITLE_MAPPER[currentApplication]?.title}
          </HeaderTitle>
        </>
      </TitleWrapper>
      <BetaLabel>
        {currentApplication && APPLICATION_TITLE_MAPPER[currentApplication]?.label}
      </BetaLabel>
    </HeaderBrandingContainer>
  );
};
